import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { axiosInstance, API } from '../App';
import axios from 'axios';
import { Search, Users, Ban, Check, Trash2, RotateCcw, X, RefreshCw, Lock, Unlock, FlaskConical, UserCheck, ShieldCheck, Download, Landmark, Shield, Plus, Copy, FileText, AlertTriangle, ChevronDown, Loader2 } from 'lucide-react';

import ChangeEmailModal, { sendAdminPasswordReset, toastForReset } from '../components/admin/ChangeEmailModal';
import NotificationDiagnosticPanel from '../components/admin/NotificationDiagnosticPanel';
import SafetyEmailBackupBackfillPanel from '../components/admin/SafetyEmailBackupBackfillPanel';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [stateCounts, setStateCounts] = useState({
    active: 0, deactivated: 0, loginLocked: 0, deleted: 0,
    total: 0, real: 0, test: 0, admin: 0, pending: 0, suspicious: 0,
  });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [classification, setClassification] = useState(''); // real | test | admin | ''
  const [engagement, setEngagement] = useState('');         // followed | never_followed | received_alert | never_received_alert | active_30 | inactive_30 | ''
  const [deactivated, setDeactivated] = useState('');
  const [lockedOnly, setLockedOnly] = useState(false);
  const [approvalFilter, setApprovalFilter] = useState(''); // pending | approved | ''
  const [suspiciousOnly, setSuspiciousOnly] = useState(false);
  const [approvalMode, setApprovalMode] = useState(null); // null=loading
  const [approvalModeBusy, setApprovalModeBusy] = useState(false);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionLoading, setActionLoading] = useState(null);
  const [reasonPrompt, setReasonPrompt] = useState(null);
  const [reasonInput, setReasonInput] = useState('');
  const [exporting, setExporting] = useState(false);
  // Iter 201 — Almería towns list (loaded once) + Create Official modal state.
  const [towns, setTowns] = useState([]);
  const [showOfficialModal, setShowOfficialModal] = useState(false);
  const [officialForm, setOfficialForm] = useState({
    email: '',
    displayName: '',
    officialSafetySource: 'town_hall',
    officialSafetyTown: '',
    password: '',
  });
  const [officialBusy, setOfficialBusy] = useState(false);
  const [createdOfficial, setCreatedOfficial] = useState(null); // shows temp password once
  // Iter — Bulk cleanup tool for test/iteration accounts.
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [hardDeleteModal, setHardDeleteModal] = useState(null); // { loading, impact, userIds }
  // Feb 2026 — admin-assisted account recovery
  const [changeEmailFor, setChangeEmailFor] = useState(null); // user object or null
  // Feb 2026 (v2) — Recent Recovery Actions panel
  const [recentActions, setRecentActions] = useState([]);
  const [recentActionsLoading, setRecentActionsLoading] = useState(false);
  const loadRecentActions = useCallback(async () => {
    setRecentActionsLoading(true);
    try {
      const r = await axiosInstance.get('/admin/recovery-actions?limit=20');
      setRecentActions(r.data?.items || []);
    } catch {
      // silent — non-critical panel
    } finally {
      setRecentActionsLoading(false);
    }
  }, []);
  useEffect(() => { loadRecentActions(); }, [loadRecentActions]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/safety-bulletin/towns`);
        if (alive) setTowns(r.data.towns || []);
      } catch { /* non-fatal */ }
    })();
    (async () => {
      try {
        const r = await axiosInstance.get('/admin/config');
        if (alive) setApprovalMode(!!r.data?.userApprovalMode);
      } catch { if (alive) setApprovalMode(false); }
    })();
    return () => { alive = false; };
  }, []);

  const toggleApprovalMode = async () => {
    if (approvalModeBusy) return;
    const next = !approvalMode;
    setApprovalModeBusy(true);
    try {
      await axiosInstance.put('/admin/config', { userApprovalMode: next });
      setApprovalMode(next);
      flash('success', next ? 'Approval Mode ON — new signups require admin approval.' : 'Approval Mode OFF — new signups are active immediately.');
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Could not update approval mode');
    } finally {
      setApprovalModeBusy(false);
    }
  };

  const submitCreateOfficial = async () => {
    if (officialBusy) return;
    const payload = {
      email: (officialForm.email || '').trim().toLowerCase(),
      displayName: (officialForm.displayName || '').trim(),
      officialSafetySource: officialForm.officialSafetySource,
      officialSafetyTown: officialForm.officialSafetyTown,
    };
    if (officialForm.password && officialForm.password.trim()) {
      payload.password = officialForm.password.trim();
    }
    if (!payload.email || !payload.displayName || !payload.officialSafetyTown) {
      flash('error', 'Email, display name and assigned town are required.');
      return;
    }
    setOfficialBusy(true);
    try {
      const r = await axiosInstance.post('/admin/users/official', payload);
      setCreatedOfficial({
        email: r.data.user.email,
        displayName: r.data.user.fullName,
        source: r.data.user.officialSafetySource,
        town: r.data.user.officialSafetyTown,
        temporaryPassword: r.data.temporaryPassword,
      });
      setOfficialForm({ email: '', displayName: '', officialSafetySource: 'town_hall', officialSafetyTown: '', password: '' });
      setShowOfficialModal(false);
      fetchUsers();
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Failed to create official account.');
    } finally {
      setOfficialBusy(false);
    }
  };

  const handleExportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = {};
      if (role) params.role = role;
      if (classification) params.classification = classification;
      if (engagement) params.engagement = engagement;
      if (deactivated) params.deactivated = deactivated;
      if (lockedOnly) params.locked = 'only';
      if (search.trim()) params.search = search.trim();
      if (includeDeleted) params.include_deleted = true;
      const response = await axiosInstance.get('/admin/users/export.csv', {
        params,
        responseType: 'blob',
      });
      const total = response.headers?.['x-total-rows'];
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Try to grab the filename from Content-Disposition; fall back to a sensible default.
      const cd = response.headers?.['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/i);
      link.download = match ? match[1] : `f2d-users-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setMessage({ type: 'success', text: `Exported ${total || '?'} user(s) to CSV.` });
    } catch (e) {
      setMessage({ type: 'error', text: 'Could not export CSV — please try again.' });
    } finally {
      setExporting(false);
    }
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (role) params.role = role;
      if (classification) params.classification = classification;
      if (engagement) params.engagement = engagement;
      if (deactivated) params.deactivated = deactivated;
      if (lockedOnly) params.locked = 'only';
      if (approvalFilter) params.approval = approvalFilter;
      if (suspiciousOnly) params.suspicious = 'only';
      if (search.trim()) params.search = search.trim();
      if (includeDeleted) params.include_deleted = true;
      const [listRes, countsRes] = await Promise.all([
        axiosInstance.get('/admin/users', { params }),
        axiosInstance.get('/admin/users/state-counts').catch(() => ({ data: stateCounts })),
      ]);
      setUsers(listRes.data.items || []);
      setTotal(listRes.data.total || 0);
      setTotalPages(listRes.data.totalPages || 1);
      setSelected(new Set());
      if (countsRes?.data) setStateCounts(countsRes.data);
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, role, classification, engagement, deactivated, lockedOnly, approvalFilter, suspiciousOnly, search, includeDeleted]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const flash = (type, text) => { setMessage({ type, text }); setTimeout(() => setMessage(null), 4000); };

  const runAction = async (user, action, reason = '') => {
    setActionLoading(`${action}-${user.id}`);
    try {
      if (action === 'deactivate') {
        await axiosInstance.put(`/admin/user/${user.id}/deactivate`, { reason });
        flash('success', `${user.email} deactivated`);
      } else if (action === 'reactivate') {
        await axiosInstance.put(`/admin/user/${user.id}/reactivate`);
        flash('success', `${user.email} reactivated`);
      } else if (action === 'delete') {
        if (!window.confirm(`Soft-delete ${user.email}?\n\nLogin will be blocked and owned businesses will be hidden. Recoverable via Restore.`)) { setActionLoading(null); return; }
        await axiosInstance.delete(`/admin/user/${user.id}`);
        flash('success', `${user.email} soft-deleted`);
      } else if (action === 'restore') {
        await axiosInstance.post(`/admin/user/${user.id}/restore`);
        flash('success', `${user.email} restored`);
      } else if (action === 'unlock') {
        const res = await axiosInstance.put(`/admin/user/${user.id}/unlock`);
        flash('success', res.data?.message || `${user.email} unlocked`);
      }
      fetchUsers();
    } catch (err) {
      flash('error', err.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setActionLoading(null);
    }
  };

  const submitReason = () => {
    if (!reasonPrompt) return;
    runAction(reasonPrompt.user, reasonPrompt.action, reasonInput.trim());
    setReasonPrompt(null); setReasonInput('');
  };

  const roleColor = (r) => r === 'admin' ? 'bg-red-100 text-red-700' : r === 'business' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700';

  // Feb 2026 — trigger the SAME reset email the self-service flow sends.
  const sendPasswordReset = async (user) => {
    if (!window.confirm(`Send a password-reset link to ${user.email}?`)) return;
    setActionLoading(`reset-${user.id}`);
    try {
      const data = await sendAdminPasswordReset(user.id);
      const { level, message } = toastForReset(data, user.email);
      flash(level, message);
      loadRecentActions();
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setActionLoading(null);
    }
  };

  // Feb 2026 — change registered email is handled by the shared modal.



  const toggleTestFlag = async (user, markAsTest) => {
    setActionLoading(`testflag-${user.id}`);
    try {
      await axiosInstance.put(`/admin/user/${user.id}/test-flag`, { isTestAccount: markAsTest });
      flash('success', `${user.email} marked as ${markAsTest ? 'TEST' : 'REAL'}`);
      fetchUsers();
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Failed to update flag');
    } finally {
      setActionLoading(null);
    }
  };

  // Iter 200 — Promote / demote a user as an Official Safety Source.
  // Iter 201 — Promotion now also requires an assigned town.
  const setOfficialSource = async (user, value, town = null) => {
    setActionLoading(`offsrc-${user.id}`);
    try {
      const body = { officialSafetySource: value || null };
      if (value) {
        body.officialSafetyTown = town || user.officialSafetyTown || '';
        if (!body.officialSafetyTown) {
          flash('error', 'Pick an assigned town before promoting this user.');
          setActionLoading(null);
          return;
        }
      }
      await axiosInstance.put(`/admin/user/${user.id}/official-source`, body);
      const label = value === 'town_hall' ? 'Town Hall' : value === 'police' ? 'Police' : 'none';
      const suffix = value && body.officialSafetyTown ? ` (${body.officialSafetyTown})` : '';
      flash('success', `${user.email} official source set to ${label}${suffix}`);
      fetchUsers();
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Failed to update official source');
    } finally {
      setActionLoading(null);
    }
  };

  const classificationBadge = (cls) => {
    if (cls === 'admin') return { label: 'ADMIN', cls: 'bg-red-100 text-red-800 ring-red-200' };
    if (cls === 'test') return { label: 'TEST', cls: 'bg-amber-100 text-amber-800 ring-amber-200' };
    return { label: 'REAL', cls: 'bg-emerald-100 text-emerald-800 ring-emerald-200' };
  };

  // ---- Bulk selection helpers ----
  // Real admins + the sysadmin account are never selectable; obvious test/iteration
  // admins (e.g. iter*@example.com) ARE selectable so they can be cleaned up.
  const isSelectable = (u) => {
    const email = (u.email || '').toLowerCase();
    if (email === 'sysadmin@find2day.com') return false;
    if (u.role === 'admin' && !u.emailLooksTest) return false;
    return true;
  };
  const selectableIds = users.filter(isSelectable).map((u) => u.id);
  const allPageSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleAllPage = () => setSelected((prev) => {
    const next = new Set(prev);
    if (allPageSelected) { selectableIds.forEach((id) => next.delete(id)); }
    else { selectableIds.forEach((id) => next.add(id)); }
    return next;
  });
  const clearSelection = () => setSelected(new Set());

  const runBulk = async (action) => {
    const userIds = [...selected];
    if (!userIds.length || bulkBusy) return;
    if (action === 'soft-delete' && !window.confirm(`Soft-delete ${userIds.length} user(s)?\n\nLogin is blocked and owned businesses hidden. Recoverable via Restore.`)) return;
    if (action === 'reject' && !window.confirm(`Reject ${userIds.length} pending user(s)?\n\nThis soft-deletes the account(s). Recoverable via Restore.`)) return;
    setBulkBusy(true);
    try {
      let res;
      if (action === 'deactivate') res = await axiosInstance.post('/admin/users/bulk-deactivate', { userIds });
      else if (action === 'soft-delete') res = await axiosInstance.post('/admin/users/bulk-soft-delete', { userIds });
      else if (action === 'restore') res = await axiosInstance.post('/admin/users/bulk-restore', { userIds });
      else if (action === 'approve') res = await axiosInstance.post('/admin/users/bulk-approve', { userIds });
      else if (action === 'reject') res = await axiosInstance.post('/admin/users/bulk-reject', { userIds });
      const skipped = res?.data?.skipped?.length || 0;
      const n = res?.data?.deactivated ?? res?.data?.softDeleted ?? res?.data?.restored ?? res?.data?.approved ?? res?.data?.rejected ?? userIds.length;
      const verb = { 'restore': 'restored', 'deactivate': 'deactivated', 'soft-delete': 'soft-deleted', 'approve': 'approved', 'reject': 'rejected' }[action];
      flash('success', `${n} user(s) ${verb}${skipped ? ` · ${skipped} protected/skipped` : ''}.`);
      clearSelection();
      fetchUsers();
    } catch (err) {
      flash('error', err.response?.data?.detail || `Bulk ${action} failed`);
    } finally {
      setBulkBusy(false);
    }
  };

  const approveUser = async (user) => {
    setActionLoading(`approve-${user.id}`);
    try {
      await axiosInstance.put(`/admin/user/${user.id}/approve`);
      flash('success', `${user.email} approved`);
      fetchUsers();
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Failed to approve');
    } finally {
      setActionLoading(null);
    }
  };

  const rejectUser = async (user) => {
    if (!window.confirm(`Reject ${user.email}?\n\nThis soft-deletes the account (recoverable via Restore).`)) return;
    setActionLoading(`reject-${user.id}`);
    try {
      await axiosInstance.put(`/admin/user/${user.id}/reject`, { reason: '' });
      flash('success', `${user.email} rejected`);
      fetchUsers();
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Failed to reject');
    } finally {
      setActionLoading(null);
    }
  };

  const openHardDelete = async () => {
    const userIds = [...selected];
    if (!userIds.length) return;
    setHardDeleteModal({ loading: true });
    try {
      const r = await axiosInstance.post('/admin/users/bulk-impact', { userIds });
      setHardDeleteModal({ loading: false, impact: r.data, userIds });
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Could not load delete impact');
      setHardDeleteModal(null);
    }
  };

  const confirmHardDelete = async () => {
    if (bulkBusy || !hardDeleteModal?.userIds) return;
    setBulkBusy(true);
    try {
      const r = await axiosInstance.post('/admin/users/bulk-hard-delete', { userIds: hardDeleteModal.userIds, confirm: 'PERMANENT-DELETE' });
      const blocked = r.data?.blocked?.length || 0;
      const skipped = r.data?.skipped?.length || 0;
      flash('success', `Permanently deleted ${r.data.hardDeleted} user(s) + ${r.data.deletedBusinesses} business(es)${blocked ? ` · ${blocked} blocked (real business)` : ''}${skipped ? ` · ${skipped} protected` : ''}.`);
      clearSelection();
      setHardDeleteModal(null);
      fetchUsers();
    } catch (err) {
      flash('error', err.response?.data?.detail || 'Bulk hard-delete failed');
    } finally {
      setBulkBusy(false);
    }
  };


  return (
    <div className="p-6 space-y-4" data-testid="admin-users-tab">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Users className="h-6 w-6" /> User Management</h2>
          <p className="text-sm text-gray-500 mt-1">View, deactivate, soft-delete and restore user accounts. Showing <strong>{total}</strong> matching this filter.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/admin/authority-onboarding"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-300 bg-white text-amber-800 hover:bg-amber-50 text-sm font-semibold shadow-sm"
            data-testid="open-authority-guide-link"
            title="Open the printable Onboard Authority guide in a new tab"
          >
            <FileText className="h-4 w-4" />
            Onboarding Guide
          </Link>
          <button
            type="button"
            onClick={() => setShowOfficialModal(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold shadow-sm"
            data-testid="create-official-account-btn"
            title="Create a Town Hall or Police account that posts as an OFFICIAL safety source"
          >
            <Landmark className="h-4 w-4" />
            <Plus className="h-3.5 w-3.5 -ml-1" />
            Create Official Account
          </button>
        </div>
      </div>

      {/* Approval Mode toggle + pending/suspicious filters */}
      <div className="bg-white rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="approval-mode-panel">
        <div className="flex items-start gap-3">
          <ShieldCheck className={`h-5 w-5 mt-0.5 ${approvalMode ? 'text-emerald-600' : 'text-gray-400'}`} />
          <div>
            <div className="text-sm font-semibold text-gray-900">Signup Approval Mode</div>
            <p className="text-xs text-gray-500 max-w-md">
              {approvalMode
                ? 'ON — new user signups are held as Pending and cannot log in until you approve them. Suspicious/test emails are always held.'
                : 'OFF — new user signups are active immediately. Suspicious/test emails are still held for review.'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={toggleApprovalMode}
          disabled={approvalMode === null || approvalModeBusy}
          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors disabled:opacity-50 ${approvalMode ? 'bg-emerald-600' : 'bg-gray-300'}`}
          data-testid="approval-mode-toggle"
          aria-pressed={!!approvalMode}
          title="Toggle whether new signups require admin approval"
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${approvalMode ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>

      {/* Moderation filters: Pending approval + Suspicious */}
      <div className="grid grid-cols-2 gap-2" data-testid="moderation-counts">
        <button
          onClick={() => { setApprovalFilter(approvalFilter === 'pending' ? '' : 'pending'); setSuspiciousOnly(false); setPage(1); }}
          className={`rounded-lg border px-3 py-2 text-left transition-colors ${approvalFilter === 'pending' ? 'bg-amber-100 border-amber-400 ring-2 ring-amber-300' : 'bg-amber-50 border-amber-200 hover:bg-amber-100'}`}
          data-testid="users-filter-pending"
        >
          <div className="text-[11px] font-medium text-amber-700 uppercase tracking-wide flex items-center gap-1.5"><Lock className="h-3 w-3" /> Pending approval</div>
          <div className="text-lg font-bold text-amber-900 tabular-nums">{stateCounts.pending || 0}</div>
        </button>
        <button
          onClick={() => { setSuspiciousOnly(!suspiciousOnly); setApprovalFilter(''); setPage(1); }}
          className={`rounded-lg border px-3 py-2 text-left transition-colors ${suspiciousOnly ? 'bg-rose-100 border-rose-400 ring-2 ring-rose-300' : 'bg-rose-50 border-rose-200 hover:bg-rose-100'}`}
          data-testid="users-filter-suspicious"
        >
          <div className="text-[11px] font-medium text-rose-700 uppercase tracking-wide flex items-center gap-1.5"><AlertTriangle className="h-3 w-3" /> Suspicious</div>
          <div className="text-lg font-bold text-rose-900 tabular-nums">{stateCounts.suspicious || 0}</div>
        </button>
      </div>

      {/* Real / Test / Admin classification — click to filter */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="users-classification-counts">
        <button
          onClick={() => { setClassification(''); setPage(1); }}
          className={`rounded-lg border px-3 py-2 text-left transition-colors ${classification === '' ? 'bg-slate-100 border-slate-400 ring-2 ring-slate-300' : 'bg-slate-50 border-slate-200 hover:bg-slate-100'}`}
          data-testid="users-filter-all"
        >
          <div className="text-[11px] font-medium text-slate-700 uppercase tracking-wide flex items-center gap-1.5"><Users className="h-3 w-3" /> All users</div>
          <div className="text-lg font-bold text-slate-900 tabular-nums">{stateCounts.total || 0}</div>
        </button>
        <button
          onClick={() => { setClassification('real'); setPage(1); }}
          className={`rounded-lg border px-3 py-2 text-left transition-colors ${classification === 'real' ? 'bg-emerald-100 border-emerald-400 ring-2 ring-emerald-300' : 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100'}`}
          data-testid="users-filter-real"
        >
          <div className="text-[11px] font-medium text-emerald-700 uppercase tracking-wide flex items-center gap-1.5"><UserCheck className="h-3 w-3" /> Real users</div>
          <div className="text-lg font-bold text-emerald-900 tabular-nums">{stateCounts.real || 0}</div>
        </button>
        <button
          onClick={() => { setClassification('test'); setPage(1); }}
          className={`rounded-lg border px-3 py-2 text-left transition-colors ${classification === 'test' ? 'bg-amber-100 border-amber-400 ring-2 ring-amber-300' : 'bg-amber-50 border-amber-200 hover:bg-amber-100'}`}
          data-testid="users-filter-test"
        >
          <div className="text-[11px] font-medium text-amber-700 uppercase tracking-wide flex items-center gap-1.5"><FlaskConical className="h-3 w-3" /> Test users</div>
          <div className="text-lg font-bold text-amber-900 tabular-nums">{stateCounts.test || 0}</div>
        </button>
        <button
          onClick={() => { setClassification('admin'); setPage(1); }}
          className={`rounded-lg border px-3 py-2 text-left transition-colors ${classification === 'admin' ? 'bg-red-100 border-red-400 ring-2 ring-red-300' : 'bg-red-50 border-red-200 hover:bg-red-100'}`}
          data-testid="users-filter-admin"
        >
          <div className="text-[11px] font-medium text-red-700 uppercase tracking-wide flex items-center gap-1.5"><ShieldCheck className="h-3 w-3" /> Admins</div>
          <div className="text-lg font-bold text-red-900 tabular-nums">{stateCounts.admin || 0}</div>
        </button>
      </div>

      {/* State summary — makes Reactivate/Unlock paths discoverable */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="users-state-counts">
        <button onClick={() => { setDeactivated(''); setLockedOnly(false); setIncludeDeleted(false); setPage(1); }}
                className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-left hover:bg-emerald-100 transition-colors">
          <div className="text-[11px] font-medium text-emerald-700 uppercase tracking-wide">Active</div>
          <div className="text-lg font-bold text-emerald-900 tabular-nums">{stateCounts.active}</div>
        </button>
        <button onClick={() => { setDeactivated('only'); setLockedOnly(false); setIncludeDeleted(false); setPage(1); }}
                className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-left hover:bg-amber-100 transition-colors"
                data-testid="users-filter-deactivated">
          <div className="text-[11px] font-medium text-amber-700 uppercase tracking-wide">Deactivated</div>
          <div className="text-lg font-bold text-amber-900 tabular-nums">{stateCounts.deactivated}</div>
          <div className="text-[10px] text-amber-600 mt-0.5">Click to filter · Reactivate from row</div>
        </button>
        <button onClick={() => { setLockedOnly(true); setDeactivated(''); setIncludeDeleted(false); setPage(1); }}
                className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 text-left hover:bg-orange-100 transition-colors"
                data-testid="users-filter-locked">
          <div className="text-[11px] font-medium text-orange-700 uppercase tracking-wide">Login locked</div>
          <div className="text-lg font-bold text-orange-900 tabular-nums">{stateCounts.loginLocked}</div>
          <div className="text-[10px] text-orange-600 mt-0.5">Click to filter · Unlock from row</div>
        </button>
        <button onClick={() => { setIncludeDeleted(true); setDeactivated(''); setLockedOnly(false); setPage(1); }}
                className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-left hover:bg-red-100 transition-colors">
          <div className="text-[11px] font-medium text-red-700 uppercase tracking-wide">Deleted</div>
          <div className="text-lg font-bold text-red-900 tabular-nums">{stateCounts.deleted}</div>
          <div className="text-[10px] text-red-600 mt-0.5">Click to include in list</div>
        </button>
      </div>

      {message && (
        <div className={`p-3 rounded-lg text-sm ${message.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>{message.text}</div>
      )}

      {/* Jul 2026 — Notification-delivery diagnostic (collapsible, defaults collapsed). */}
      <NotificationDiagnosticPanel />

      {/* Feb 2026 (v6.1) — one-click migration: enable safety email backup for all registered users */}
      <SafetyEmailBackupBackfillPanel />

      {/* Filters */}
      <div className="bg-white rounded-xl border p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text" placeholder="Search email, name, phone, business, user ID or previous email…"
              value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
              data-testid="users-search"
            />
          </div>
          <select value={role} onChange={(e) => { setRole(e.target.value); setPage(1); }} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="users-role-filter">
            <option value="">All roles</option>
            <option value="user">Users</option>
            <option value="business">Business Owners</option>
            <option value="admin">Admins</option>
          </select>
          <select value={classification} onChange={(e) => { setClassification(e.target.value); setPage(1); }} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="users-classification-filter">
            <option value="">All types (REAL / TEST / ADMIN)</option>
            <option value="real">Real users only</option>
            <option value="test">Test users only</option>
            <option value="admin">Admins only</option>
          </select>
          <select value={engagement} onChange={(e) => { setEngagement(e.target.value); setPage(1); }} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="users-engagement-filter">
            <option value="">All engagement levels</option>
            <option value="followed">Followed a town</option>
            <option value="never_followed">Never followed a town</option>
            <option value="received_alert">Received a safety alert</option>
            <option value="never_received_alert">Never received an alert</option>
            <option value="active_30">Active in last 30 days</option>
            <option value="inactive_30">Inactive 30+ days</option>
          </select>
          <select value={deactivated} onChange={(e) => { setDeactivated(e.target.value); setPage(1); }} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="users-deactivated-filter">
            <option value="">All accounts</option>
            <option value="exclude">Hide deactivated</option>
            <option value="only">Deactivated only</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={lockedOnly} onChange={(e) => { setLockedOnly(e.target.checked); setPage(1); }} data-testid="users-locked-only" />
            Login-locked only
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={includeDeleted} onChange={(e) => { setIncludeDeleted(e.target.checked); setPage(1); }} data-testid="users-include-deleted" />
            Include soft-deleted
          </label>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-2 pt-2 border-t border-gray-100">
          <p className="text-xs text-gray-500">
            CSV export honours every filter above. Includes engagement columns (towns, alerts, last login, login count).
          </p>
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={exporting}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-semibold shadow-sm"
            data-testid="users-export-csv-btn"
            title="Download a CSV of all users matching the current filters"
          >
            <Download className="h-4 w-4" />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* Bulk action bar — appears when rows are selected */}
      {selected.size > 0 && (
        <div className="sticky top-0 z-20 bg-slate-900 text-white rounded-xl px-4 py-3 flex flex-wrap items-center gap-3 shadow-lg" data-testid="bulk-action-bar">
          <span className="text-sm font-semibold" data-testid="bulk-selected-count">{selected.size} selected</span>
          <button onClick={clearSelection} className="text-xs text-slate-300 hover:text-white underline" data-testid="bulk-clear">Clear</button>
          <div className="h-5 w-px bg-slate-600 mx-1" />
          <button onClick={() => runBulk('approve')} disabled={bulkBusy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 text-sm font-semibold disabled:opacity-60"
                  data-testid="bulk-approve-btn">
            <Check className="h-4 w-4" /> Approve
          </button>
          <button onClick={() => runBulk('reject')} disabled={bulkBusy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-rose-400 text-rose-200 hover:bg-rose-400/10 text-sm font-medium disabled:opacity-60"
                  data-testid="bulk-reject-btn">
            <X className="h-4 w-4" /> Reject
          </button>
          <div className="h-5 w-px bg-slate-600 mx-1" />
          <button onClick={() => runBulk('soft-delete')} disabled={bulkBusy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-slate-900 hover:bg-slate-100 text-sm font-semibold disabled:opacity-60"
                  data-testid="bulk-soft-delete-btn">
            {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Soft-Delete
          </button>
          <button onClick={() => runBulk('deactivate')} disabled={bulkBusy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-400 text-amber-200 hover:bg-amber-400/10 text-sm font-medium disabled:opacity-60"
                  data-testid="bulk-deactivate-btn">
            <Ban className="h-4 w-4" /> Deactivate
          </button>
          <button onClick={() => runBulk('restore')} disabled={bulkBusy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-400 text-emerald-200 hover:bg-emerald-400/10 text-sm font-medium disabled:opacity-60"
                  data-testid="bulk-restore-btn">
            <RotateCcw className="h-4 w-4" /> Restore
          </button>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => setShowAdvanced((v) => !v)}
                    className="inline-flex items-center gap-1 text-xs text-slate-300 hover:text-white"
                    data-testid="bulk-advanced-toggle">
              Advanced <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
            </button>
            {showAdvanced && (
              <button onClick={openHardDelete} disabled={bulkBusy}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-60"
                      data-testid="bulk-hard-delete-btn">
                <AlertTriangle className="h-4 w-4" /> Hard-Delete (permanent)
              </button>
            )}
          </div>
        </div>
      )}

      {/* Feb 2026 — Recent Recovery Actions panel */}
      <div className="bg-white rounded-xl border mb-4" data-testid="recovery-actions-panel">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div>
            <h2 className="text-sm font-bold text-gray-900">Recent account-recovery actions</h2>
            <p className="text-[11px] text-gray-500">Last 20 password resets and email changes — includes delivery outcome.</p>
          </div>
          <button
            onClick={loadRecentActions}
            disabled={recentActionsLoading}
            className="text-xs text-purple-700 hover:text-purple-800 font-semibold disabled:opacity-50"
            data-testid="recovery-actions-refresh"
          >{recentActionsLoading ? 'Refreshing…' : 'Refresh'}</button>
        </div>
        {recentActions.length === 0 ? (
          <div className="p-4 text-xs text-gray-500">No recovery actions yet.</div>
        ) : (
          <div className="max-h-72 overflow-y-auto divide-y">
            {recentActions.map((a) => {
              const isChange = a.action === 'change_email';
              const isFailed = a.action === 'send_password_reset_failed';
              const isReset = a.action === 'send_password_reset';
              const badge = isFailed ? {label:'Delivery failed', cls:'bg-red-100 text-red-700 ring-red-200'} :
                            isChange ? {label:'Email changed', cls:'bg-indigo-100 text-indigo-700 ring-indigo-200'} :
                                       {label:'Reset sent', cls:'bg-emerald-100 text-emerald-700 ring-emerald-200'};
              const deliveryBadge = a.deliveryStatus === 'sent' ? 'Handed to provider' :
                                    a.deliveryStatus === 'logged' ? 'Logged only (no provider)' :
                                    a.deliveryStatus === 'error' ? 'Provider error' : null;
              return (
                <div key={a.id} className="px-4 py-2.5 text-xs" data-testid={`recovery-row-${a.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded ring-1 text-[10px] font-bold ${badge.cls}`}>
                      {badge.label}
                    </span>
                    {deliveryBadge && (
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded ring-1 text-[10px] font-medium ${
                        a.deliveryStatus === 'sent' ? 'bg-green-50 text-green-700 ring-green-200' :
                        a.deliveryStatus === 'logged' ? 'bg-gray-100 text-gray-600 ring-gray-200' :
                                                       'bg-red-50 text-red-700 ring-red-200'
                      }`}>{deliveryBadge}</span>
                    )}
                    <span className="text-gray-500">{a.at ? new Date(a.at).toLocaleString() : ''}</span>
                  </div>
                  <div className="mt-1 text-gray-800">
                    {isChange ? (
                      <span>
                        <span className="font-mono">{a.oldEmail}</span> → <span className="font-mono">{a.newEmail}</span>
                      </span>
                    ) : (
                      <span>To <span className="font-mono">{a.targetEmail || '—'}</span></span>
                    )}
                  </div>
                  <div className="mt-0.5 text-gray-500">
                    by <span className="font-medium">{a.adminEmail || a.adminId || 'admin'}</span>
                    {isChange && a.reason && (
                      <span className="ml-2 italic">— {a.reason}</span>
                    )}
                    {isReset && a.context === 'email_change_reset' && (
                      <span className="ml-2 italic text-indigo-600">— follow-up to email change</span>
                    )}
                    {isFailed && a.providerResponse && (
                      <span className="ml-2 text-red-600">— {a.providerResponse}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-3 py-3 text-left w-10">
                <input type="checkbox" checked={allPageSelected} onChange={toggleAllPage}
                       title="Select all selectable rows on this page"
                       data-testid="users-select-all" />
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Name / Email</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Type</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Role</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">State</th>
              <th className="px-3 py-3 text-left font-medium text-gray-600" title="Towns followed for safety alerts">
                <div className="flex flex-col leading-tight">
                  <span>Towns</span>
                  <span className="text-[10px] text-gray-400 font-normal">followed</span>
                </div>
              </th>
              <th className="px-3 py-3 text-left font-medium text-gray-600" title="Safety alerts received in the in-app inbox">
                <div className="flex flex-col leading-tight">
                  <span>Alerts</span>
                  <span className="text-[10px] text-gray-400 font-normal">received</span>
                </div>
              </th>
              <th className="px-3 py-3 text-left font-medium text-gray-600" title="Tracked from today — historic users will show a dash until their next sign-in">
                <div className="flex flex-col leading-tight">
                  <span>Last login</span>
                  <span className="text-[10px] text-amber-600 font-normal">tracked from today</span>
                </div>
              </th>
              <th className="px-3 py-3 text-left font-medium text-gray-600" title="Lifetime login count — tracked from today">
                <div className="flex flex-col leading-tight">
                  <span>Logins</span>
                  <span className="text-[10px] text-amber-600 font-normal">tracked from today</span>
                </div>
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Created</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="11" className="py-8 text-center text-gray-500">Loading...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan="11" className="py-8 text-center text-gray-500">No users match these filters.</td></tr>
            ) : users.map((u) => {
              const clsBadge = classificationBadge(u.classification || 'real');
              const isExplicit = u.isTestAccount === true || u.isTestAccount === false;
              const lastLogin = u.lastLoginAt ? new Date(u.lastLoginAt) : null;
              const now = new Date();
              let lastLoginLabel = '—';
              let lastLoginClass = 'text-gray-400';
              if (lastLogin) {
                const daysAgo = Math.floor((now - lastLogin) / 86400000);
                if (daysAgo === 0) { lastLoginLabel = 'Today'; lastLoginClass = 'text-emerald-700 font-medium'; }
                else if (daysAgo === 1) { lastLoginLabel = 'Yesterday'; lastLoginClass = 'text-emerald-700'; }
                else if (daysAgo <= 7) { lastLoginLabel = `${daysAgo}d ago`; lastLoginClass = 'text-emerald-700'; }
                else if (daysAgo <= 30) { lastLoginLabel = `${daysAgo}d ago`; lastLoginClass = 'text-gray-700'; }
                else { lastLoginLabel = `${daysAgo}d ago`; lastLoginClass = 'text-amber-700'; }
              }
              return (
              <tr key={u.id} className={`border-b hover:bg-gray-50 ${selected.has(u.id) ? 'bg-purple-50' : ''}`} data-testid={`user-row-${u.id}`}>
                <td className="px-3 py-3">
                  {isSelectable(u) ? (
                    <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggleOne(u.id)}
                           data-testid={`user-select-${u.id}`} />
                  ) : (
                    <span className="text-[10px] text-gray-300" title="Protected account">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{u.fullName || '—'}</div>
                  <div className="text-xs text-gray-500">{u.email}</div>
                  {u.matchHints && u.matchHints.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1" data-testid={`user-match-hints-${u.id}`}>
                      {u.matchHints.slice(0, 3).map((h, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-purple-50 text-purple-700 ring-1 ring-purple-200"
                          title={h.kind === 'previous_email' ? 'Matched a previous email address' : 'Matched a business record'}
                        >
                          {h.kind === 'previous_email' ? '↩ prev email' : '🏢 business'}
                          <span className="font-medium truncate max-w-[10rem]">{h.value}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ring-1 ${clsBadge.cls}`}
                    data-testid={`user-classification-badge-${u.id}`}
                    title={isExplicit ? 'Manually set' : 'Auto-detected from email'}
                  >
                    {clsBadge.label}
                    {!isExplicit && u.classification !== 'admin' && <span className="font-normal opacity-60">·auto</span>}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${roleColor(u.role)}`}>{u.role || 'user'}</span>
                  {u.isOfficialAccount && (
                    <span
                      className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 ring-1 ring-amber-200 font-bold uppercase tracking-wide inline-flex items-center gap-0.5"
                      data-testid={`user-official-badge-${u.id}`}
                      title="Admin-created Official Safety Source account"
                    >
                      {u.officialSafetySource === 'police' ? <Shield className="h-2.5 w-2.5" /> : <Landmark className="h-2.5 w-2.5" />}
                      Official
                    </span>
                  )}
                  {/* Iter 200 + 201 — Official Safety Source admin control.
                      Only surfaces on non-admin rows; admins are protected.
                      Promotion now requires an assigned town. */}
                  {u.role !== 'admin' && (
                    <div className="mt-1 space-y-1">
                      <select
                        value={u.officialSafetySource || ''}
                        onChange={(e) => setOfficialSource(u, e.target.value, u.officialSafetyTown)}
                        disabled={actionLoading === `offsrc-${u.id}`}
                        className="block w-full text-[11px] px-1.5 py-1 border border-purple-200 rounded bg-white text-purple-800 focus:ring-2 focus:ring-purple-400 outline-none disabled:opacity-50"
                        data-testid={`user-official-source-${u.id}`}
                        title="Promote as Official Safety Source"
                      >
                        <option value="">— None —</option>
                        <option value="town_hall">🏛️ Town Hall</option>
                        <option value="police">👮 Police</option>
                      </select>
                      {u.officialSafetySource && (
                        <select
                          value={u.officialSafetyTown || ''}
                          onChange={(e) => setOfficialSource(u, u.officialSafetySource, e.target.value)}
                          disabled={actionLoading === `offsrc-${u.id}`}
                          className="block w-full text-[11px] px-1.5 py-1 border border-amber-200 rounded bg-amber-50 text-amber-900 focus:ring-2 focus:ring-amber-400 outline-none disabled:opacity-50"
                          data-testid={`user-official-town-${u.id}`}
                          title="Assigned town (alerts only reach this town's followers)"
                        >
                          <option value="">— Assign town —</option>
                          {towns.map(tn => <option key={tn} value={tn}>{tn}</option>)}
                        </select>
                      )}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1 items-start">
                    {u.deletedAt ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 font-medium">deleted</span>
                    ) : u.deactivated ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-medium">deactivated</span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">active</span>
                    )}
                    {u.loginLocked && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 font-medium inline-flex items-center gap-1" data-testid={`user-locked-badge-${u.id}`} title={`Locked until ${u.loginLockedUntil}`}>
                        <Lock className="h-3 w-3" /> login locked
                      </span>
                    )}
                    {u.approvalStatus === 'pending' && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 ring-1 ring-amber-200 font-medium inline-flex items-center gap-1" data-testid={`user-pending-badge-${u.id}`} title="Awaiting admin approval — cannot log in yet">
                        <Lock className="h-3 w-3" /> pending approval
                      </span>
                    )}
                    {u.suspicious && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 ring-1 ring-rose-200 font-medium inline-flex items-center gap-1" data-testid={`user-suspicious-badge-${u.id}`} title={u.suspiciousReason || 'Matches a suspicious/test pattern'}>
                        <AlertTriangle className="h-3 w-3" /> suspicious
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3" data-testid={`user-towns-${u.id}`}>
                  {u.hasFollowedTown ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200 text-xs font-medium" title={`${u.townCount} town(s) followed`}>
                      <Check className="h-3 w-3" /> {u.townCount}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </td>
                <td className="px-3 py-3" data-testid={`user-alerts-${u.id}`}>
                  {u.hasReceivedAlert ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-50 text-purple-800 ring-1 ring-purple-200 text-xs font-medium" title={`${u.bulletinAlertCount} safety alert(s) received`}>
                      <Check className="h-3 w-3" /> {u.bulletinAlertCount}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </td>
                <td className={`px-3 py-3 text-xs ${lastLoginClass}`} data-testid={`user-last-login-${u.id}`} title={u.lastLoginAt || 'Not recorded yet — tracked from today'}>
                  {lastLoginLabel}
                </td>
                <td className="px-3 py-3 text-xs text-gray-700 tabular-nums" data-testid={`user-login-count-${u.id}`}>
                  {u.loginCount > 0 ? u.loginCount : <span className="text-gray-400">—</span>}
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">{u.createdAt?.slice(0, 10) || '-'}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 flex-wrap">
                    {u.role === 'admin' ? (
                      <span className="text-xs text-gray-400">(protected)</span>
                    ) : u.deletedAt ? (
                      <button onClick={() => runAction(u, 'restore')} disabled={actionLoading === `restore-${u.id}`}
                              className="px-2 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1"
                              data-testid={`user-restore-${u.id}`}>
                        {actionLoading === `restore-${u.id}` ? <RefreshCw className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                        Restore
                      </button>
                    ) : (
                      <>
                        {u.approvalStatus === 'pending' && (
                          <>
                            <button onClick={() => approveUser(u)} disabled={!!actionLoading}
                                    className="px-2 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 inline-flex items-center gap-1 font-medium"
                                    data-testid={`user-approve-${u.id}`}>
                              <Check className="h-3 w-3" /> Approve
                            </button>
                            <button onClick={() => rejectUser(u)} disabled={!!actionLoading}
                                    className="px-2 py-1 text-xs border border-rose-300 text-rose-700 rounded hover:bg-rose-50 inline-flex items-center gap-1"
                                    data-testid={`user-reject-${u.id}`}>
                              <X className="h-3 w-3" /> Reject
                            </button>
                          </>
                        )}
                        {u.loginLocked && (
                          <button onClick={() => runAction(u, 'unlock')} disabled={!!actionLoading}
                                  className="px-2 py-1 text-xs border border-orange-300 text-orange-700 rounded hover:bg-orange-50 inline-flex items-center gap-1"
                                  data-testid={`user-unlock-${u.id}`}
                                  title="Clear failed-login lock so this user can sign in immediately">
                            <Unlock className="h-3 w-3" /> Unlock
                          </button>
                        )}
                        {u.deactivated ? (
                          <button onClick={() => runAction(u, 'reactivate')} disabled={!!actionLoading}
                                  className="px-2 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 inline-flex items-center gap-1 font-medium"
                                  data-testid={`user-reactivate-${u.id}`}>
                            <Check className="h-3 w-3" /> Reactivate account
                          </button>
                        ) : (
                          <button onClick={() => { setReasonPrompt({ user: u, action: 'deactivate' }); setReasonInput(''); }} disabled={!!actionLoading}
                                  className="px-2 py-1 text-xs border border-amber-300 text-amber-700 rounded hover:bg-amber-50 inline-flex items-center gap-1"
                                  data-testid={`user-deactivate-${u.id}`}>
                            <Ban className="h-3 w-3" /> Deactivate
                          </button>
                        )}
                        <button onClick={() => sendPasswordReset(u)} disabled={actionLoading === `reset-${u.id}`}
                                className="px-2 py-1 text-xs border border-blue-300 text-blue-700 rounded hover:bg-blue-50 inline-flex items-center gap-1"
                                data-testid={`user-send-reset-${u.id}`}
                                title="Email this user a password-reset link">
                          {actionLoading === `reset-${u.id}` ? <RefreshCw className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                          Send Reset
                        </button>
                        <button onClick={() => setChangeEmailFor(u)} disabled={!!actionLoading}
                                className="px-2 py-1 text-xs border border-indigo-300 text-indigo-700 rounded hover:bg-indigo-50 inline-flex items-center gap-1"
                                data-testid={`user-change-email-${u.id}`}
                                title="Change this user's registered login email (requires verification reason)">
                          <FileText className="h-3 w-3" /> Change Email
                        </button>
                        <button onClick={() => runAction(u, 'delete')} disabled={!!actionLoading}
                                className="px-2 py-1 text-xs border border-red-300 text-red-700 rounded hover:bg-red-50 inline-flex items-center gap-1"
                                data-testid={`user-delete-${u.id}`}>
                          <Trash2 className="h-3 w-3" /> Soft-Delete
                        </button>
                        {u.role !== 'admin' && (
                          u.classification === 'test' ? (
                            <button
                              onClick={() => toggleTestFlag(u, false)}
                              disabled={actionLoading === `testflag-${u.id}`}
                              className="px-2 py-1 text-xs border border-emerald-300 text-emerald-700 rounded hover:bg-emerald-50 inline-flex items-center gap-1"
                              data-testid={`user-mark-real-${u.id}`}
                              title="Override: classify this user as a genuine public user"
                            >
                              <UserCheck className="h-3 w-3" /> Mark as Real
                            </button>
                          ) : (
                            <button
                              onClick={() => toggleTestFlag(u, true)}
                              disabled={actionLoading === `testflag-${u.id}`}
                              className="px-2 py-1 text-xs border border-amber-300 text-amber-700 rounded hover:bg-amber-50 inline-flex items-center gap-1"
                              data-testid={`user-mark-test-${u.id}`}
                              title="Flag this account so it's excluded from real-user metrics"
                            >
                              <FlaskConical className="h-3 w-3" /> Mark as Test
                            </button>
                          )
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50">Prev</button>
          <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50">Next</button>
        </div>
      )}

      {/* Iter 201 — Create Official Safety Source account */}
      {showOfficialModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="create-official-modal">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Landmark className="h-5 w-5 text-amber-700" />
                Create Official Account
              </h3>
              <button onClick={() => setShowOfficialModal(false)}><X className="h-5 w-5 text-gray-500" /></button>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              Creates a Town Hall or Police login that posts safety alerts as an OFFICIAL source. Alerts only notify users following the assigned town.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Account type</label>
                <select
                  value={officialForm.officialSafetySource}
                  onChange={(e) => setOfficialForm(f => ({ ...f, officialSafetySource: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  data-testid="create-official-source"
                >
                  <option value="town_hall">🏛️ Town Hall</option>
                  <option value="police">👮 Police / Guardia Civil</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Assigned town</label>
                <select
                  value={officialForm.officialSafetyTown}
                  onChange={(e) => setOfficialForm(f => ({ ...f, officialSafetyTown: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  data-testid="create-official-town"
                >
                  <option value="">Select town…</option>
                  {towns.map(tn => <option key={tn} value={tn}>{tn}</option>)}
                </select>
                <p className="text-[11px] text-gray-500 mt-1">Alerts from this account only reach followers of this town.</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Display name</label>
                <input
                  type="text"
                  value={officialForm.displayName}
                  onChange={(e) => setOfficialForm(f => ({ ...f, displayName: e.target.value }))}
                  placeholder={officialForm.officialSafetySource === 'police' ? 'e.g. Guardia Civil — Arboleas' : 'e.g. Arboleas Town Hall'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  data-testid="create-official-name"
                  maxLength={80}
                />
                <p className="text-[11px] text-gray-500 mt-1">Shown on every safety alert this account publishes.</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Email (login)</label>
                <input
                  type="email"
                  value={officialForm.email}
                  onChange={(e) => setOfficialForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="townhall.arboleas@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  data-testid="create-official-email"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Temporary password (optional)</label>
                <input
                  type="text"
                  value={officialForm.password}
                  onChange={(e) => setOfficialForm(f => ({ ...f, password: e.target.value }))}
                  placeholder="Leave blank to auto-generate"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                  data-testid="create-official-password"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setShowOfficialModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                data-testid="create-official-cancel"
              >
                Cancel
              </button>
              <button
                onClick={submitCreateOfficial}
                disabled={officialBusy}
                className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-60"
                data-testid="create-official-submit"
              >
                {officialBusy ? 'Creating…' : 'Create Official Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Iter 201 — Temporary password reveal (one-time) */}
      {createdOfficial && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="created-official-modal">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-emerald-700 flex items-center gap-2">
                <Check className="h-5 w-5" /> Official Account Created
              </h3>
              <button onClick={() => setCreatedOfficial(null)}><X className="h-5 w-5 text-gray-500" /></button>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Display name:</span><span className="font-semibold text-gray-900">{createdOfficial.displayName}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Email:</span><span className="font-mono text-gray-900">{createdOfficial.email}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Source:</span><span className="font-semibold text-amber-800">{createdOfficial.source === 'police' ? '👮 Police' : '🏛️ Town Hall'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Town:</span><span className="font-semibold text-gray-900">{createdOfficial.town}</span></div>
            </div>
            <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 mb-3">
              <p className="text-xs font-semibold text-amber-900 mb-1">Temporary password (shown once)</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-sm font-mono bg-white border border-amber-200 rounded px-2 py-1.5 text-amber-900 break-all" data-testid="created-official-temp-password">
                  {createdOfficial.temporaryPassword}
                </code>
                <button
                  type="button"
                  onClick={() => navigator.clipboard.writeText(createdOfficial.temporaryPassword)}
                  className="px-2 py-1 bg-amber-600 text-white text-xs rounded hover:bg-amber-700 inline-flex items-center gap-1"
                  data-testid="created-official-copy-password"
                  title="Copy password to clipboard"
                >
                  <Copy className="h-3.5 w-3.5" /> Copy
                </button>
              </div>
              <p className="text-[11px] text-amber-700 mt-2">
                Share this with the authority through a secure channel. They should change it on first sign-in.
              </p>
            </div>
            <button
              onClick={() => setCreatedOfficial(null)}
              className="w-full px-4 py-2 bg-slate-800 text-white rounded-lg text-sm font-medium hover:bg-slate-900"
              data-testid="created-official-done"
            >
              Done
            </button>
            <Link
              to="/admin/authority-onboarding"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 block text-center text-xs text-amber-700 underline hover:text-amber-900"
              data-testid="created-official-open-guide"
            >
              Open printable onboarding guide →
            </Link>
          </div>
        </div>
      )}

      {/* Bulk hard-delete confirmation */}
      {hardDeleteModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="bulk-hard-delete-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-red-700 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" /> Permanently delete users
              </h3>
              <button onClick={() => setHardDeleteModal(null)}><X className="h-5 w-5 text-gray-500" /></button>
            </div>
            {hardDeleteModal.loading ? (
              <div className="py-8 text-center text-gray-500 flex items-center justify-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin" /> Calculating impact…
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-3">
                  This <strong>cannot be undone</strong>. Linked content will be permanently removed for the{' '}
                  <strong>{Math.max(0, (hardDeleteModal.impact?.candidates || 0) - (hardDeleteModal.impact?.blocked?.length || 0))}</strong> eligible user(s).
                </p>
                <div className="grid grid-cols-3 gap-2 mb-3 text-center" data-testid="hard-delete-impact">
                  {Object.entries(hardDeleteModal.impact?.totals || {}).map(([k, v]) => (
                    <div key={k} className="bg-gray-50 border rounded-lg py-2">
                      <div className="text-lg font-bold text-gray-900 tabular-nums">{v}</div>
                      <div className="text-[10px] uppercase tracking-wide text-gray-500">{k}</div>
                    </div>
                  ))}
                </div>
                {(hardDeleteModal.impact?.blocked?.length > 0) && (
                  <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 mb-3 text-xs text-amber-900" data-testid="hard-delete-blocked">
                    <p className="font-semibold mb-1">{hardDeleteModal.impact.blocked.length} user(s) will be SKIPPED (own a real/paid business):</p>
                    <ul className="list-disc pl-4 space-y-0.5 max-h-24 overflow-auto">
                      {hardDeleteModal.impact.blocked.map((b) => <li key={b.id}>{b.email} — {b.reason}</li>)}
                    </ul>
                  </div>
                )}
                {(hardDeleteModal.impact?.skipped?.length > 0) && (
                  <p className="text-xs text-gray-500 mb-3">{hardDeleteModal.impact.skipped.length} protected/not-found account(s) excluded automatically.</p>
                )}
                <div className="flex gap-3 mt-2">
                  <button onClick={() => setHardDeleteModal(null)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50" data-testid="hard-delete-cancel">Cancel</button>
                  <button onClick={confirmHardDelete} disabled={bulkBusy || ((hardDeleteModal.impact?.candidates || 0) - (hardDeleteModal.impact?.blocked?.length || 0) <= 0)}
                          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 disabled:opacity-60 inline-flex items-center justify-center gap-2"
                          data-testid="hard-delete-confirm">
                    {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Permanently delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Deactivate reason prompt */}
      {reasonPrompt && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="user-reason-modal">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Deactivate {reasonPrompt.user.email}?</h3>
              <button onClick={() => { setReasonPrompt(null); setReasonInput(''); }}><X className="h-5 w-5 text-gray-500" /></button>
            </div>
            <p className="text-sm text-gray-600 mb-3">The user will not be able to log in until you reactivate them. A reason is optional but recommended.</p>
            <textarea rows={3} value={reasonInput} onChange={(e) => setReasonInput(e.target.value)}
                      placeholder="e.g. Spam reports" maxLength={500}
                      className="w-full p-3 border border-gray-300 rounded-lg text-sm resize-none"
                      data-testid="user-reason-input" autoFocus />
            <div className="flex gap-3 mt-4">
              <button onClick={() => { setReasonPrompt(null); setReasonInput(''); }} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={submitReason} className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700" data-testid="user-reason-confirm">Deactivate</button>
            </div>
          </div>
        </div>
      )}

      {/* Feb 2026 — Change Registered Email modal (shared component) */}
      <ChangeEmailModal
        open={!!changeEmailFor}
        target={changeEmailFor && { id: changeEmailFor.id, email: changeEmailFor.email, label: `User · ${changeEmailFor.fullName || changeEmailFor.id}` }}
        onClose={() => setChangeEmailFor(null)}
        onError={(msg) => flash('error', msg)}
        onCompleted={(data) => {
          const rd = data?.resetDelivery || {};
          let msg = `Email changed ${data.oldEmail} → ${data.newEmail}.`;
          if (rd.delivered) {
            msg += ` Password-reset link accepted by the email provider (id: ${rd.emailProviderId || 'n/a'}).`;
          } else {
            msg += ` ⚠ Password-reset delivery FAILED (${rd.deliveryStatus || 'unknown'}): ${rd.providerResponse || 'no response'}. Please retry from the reset button.`;
          }
          flash(rd.delivered ? 'success' : 'error', msg);
          fetchUsers();
          loadRecentActions();
        }}
      />
    </div>
  );
};

export default AdminUsers;
